import axios from 'axios';
import { loginRequest } from '../authConfig';
import msalInstance from '../msalInstance';
import configData from '../config.json';
import { ACCESS_TOKEN_NAME } from '../utils/enums';
import { logAppError } from '../utils/logActions';

// Concurrent 401s share one token refresh and trigger at most one logout
let tokenRefreshPromise = null;
let logoutInProgress = false;

const refreshAccessToken = () => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = (async () => {
      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (!account) throw new Error('No signed-in account to refresh token for');

      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
      localStorage.setItem(ACCESS_TOKEN_NAME, response.accessToken);
      return response.accessToken;
    })().finally(() => {
      tokenRefreshPromise = null;
    });
  }
  return tokenRefreshPromise;
};

const logoutOnce = () => {
  if (logoutInProgress) return;
  logoutInProgress = true;
  msalInstance.logoutRedirect().catch((error) => {
    // e.g. interaction_in_progress: another login/redirect is already running
    logoutInProgress = false;
    logAppError(error, 'API_401_Logout_Failed');
  });
};

export const API_URL = `${configData.REACT_APP_BACKEND_URL}/api`;
export const TNE_API_URL = `${configData.REACT_APP_TANDE_URL}/api`;
export const IDENTITY_URL = configData.REACT_APP_IDENTITY_URL;

const createAxiosClient = (baseURL) => {
  const client = axios.create({ baseURL, withCredentials: true });

  client.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(ACCESS_TOKEN_NAME);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json;charset=utf-8';
      }

      config.headers['Access-Control-Allow-Origin'] = '*';
      config.headers['Access-Control-Allow-Credentials'] = 'true';
      config.headers['Access-Control-Allow-Methods'] = 'GET,PUT,PATCH,POST,DELETE,HEAD,OPTIONS';

      return config;
    },
    (error) => {
      logAppError('API_Request_Error_in_Request', { error });
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Retry once with a fresh token; a 401 that persists after refresh is an endpoint issue, not a session one
      if (error.response?.status === 401 && originalRequest && !originalRequest.isAuthRetry) {
        originalRequest.isAuthRetry = true;
        try {
          await refreshAccessToken();
          return client(originalRequest);
        } catch (refreshError) {
          logAppError(refreshError, 'API_401_Token_Refresh_Failed');
          logoutOnce();
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

const axiosClient = createAxiosClient(API_URL);
export const tneAxiosClient = createAxiosClient(TNE_API_URL);
export const identityAxiosClient = createAxiosClient(IDENTITY_URL);

export default axiosClient;
